<script lang="ts">
import { defineComponent, nextTick } from 'vue'
import SimpleKeyboard from './SimpleKeyboard.vue'
import { LIURole, useLoggedInUserStore } from '@/stores/loggedInUser'
import { type KioskStudentIdentifierModel, KioskStudentIdentifierState } from '@/types/kioskTypes'
import { useDisplayConfigStore } from '@/stores/displayConfig'

const BUFF_ONECARD_LENGTH = 16
const STUDENT_ID_LENGTH = 9

export default defineComponent({
  name: 'KioskStudentIdentifier',
  components: { SimpleKeyboard },
  props: {
    modelValue: {
      type: Object as () => KioskStudentIdentifierModel,
      default: () => ({
        identifier: '',
        kioskState: KioskStudentIdentifierState.BOC
      })
    },
    buttonText: {
      type: String,
      default: 'Submit'
    },
    hideButton: {
      type: Boolean,
      default: false
    },
    clearWhenFullyEntered: {
      type: Boolean,
      default: false
    },
    // enables identikey option - as this is public information, it cannot be used in sensitive contexts such as login
    privileged: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    buffCardActionText() {
      return this.displayStore.isKiosk ? 'Swipe' : 'Enter'
    },
    identifier: {
      get() {
        return this.modelValue.identifier
      },
      async set(newIdentifier: string) {
        this.$emit('update:modelValue', {
          identifier: newIdentifier,
          kioskState: this.modelValue.kioskState
        })

        if (!this.clearWhenFullyEntered) return

        // automatically submit and clear input when fully entered
        // identikey unsupported, as length varies
        switch (this.kioskState) {
          case KioskStudentIdentifierState.BOC:
            if (newIdentifier.length === BUFF_ONECARD_LENGTH) {
              this.$emit(KioskStudentIdentifierState.BOC, newIdentifier)
              await nextTick()
              this.$emit('update:modelValue', { ...this.modelValue, identifier: '' })
            }
            break
          case KioskStudentIdentifierState.SID:
            if (newIdentifier.length === STUDENT_ID_LENGTH) {
              this.$emit(KioskStudentIdentifierState.SID, newIdentifier)
              await nextTick()
              this.$emit('update:modelValue', { ...this.modelValue, identifier: '' })
            }
            break
        }
      }
    },
    kioskState: {
      get() {
        return this.modelValue.kioskState
      },
      set(newKioskState: string) {
        this.$emit('update:modelValue', {
          identifier: this.modelValue.identifier,
          kioskState: newKioskState
        })
      }
    },
    keypadLayout() {
      switch (this.kioskState) {
        case KioskStudentIdentifierState.SID:
          return {
            default: ['1 2 3', '4 5 6', '7 8 9', '{bksp} 0 {enter}']
          }
        default:
          return undefined
      }
    },
    inputLabel() {
      switch (this.kioskState) {
        case KioskStudentIdentifierState.BOC:
          return `${this.buffCardActionText} your Buff OneCard`
        case KioskStudentIdentifierState.SID:
          return 'Enter Student ID'
        case KioskStudentIdentifierState.IdentiKey:
          return 'Enter IdentiKey'
        default:
          return ''
      }
    }
  },
  data() {
    return {
      KioskState: KioskStudentIdentifierState,
      LIURole,
      userStore: useLoggedInUserStore(),
      displayStore: useDisplayConfigStore()
    }
  },
  emits: [
    KioskStudentIdentifierState.BOC,
    KioskStudentIdentifierState.SID,
    KioskStudentIdentifierState.IdentiKey,
    'update:modelValue'
  ],
  methods: {
    async focusInput() {
      await nextTick()
      const input = this.$refs.identifierInputRef as HTMLInputElement
      if (input) input.focus()
    },
    doSubmit() {
      const identifier = this.identifier.trim()
      if (!identifier) return
      this.$emit(this.kioskState, this.identifier)
    },
    handleKeyPress(button: string) {
      switch (button) {
        case '{enter}':
          this.doSubmit()
          this.identifier = ''
          break
        case '{bksp}':
          this.identifier = this.identifier.slice(0, -1)
          break
        case '{space}':
          this.identifier += ' '
          break
        default:
          if (RegExp('^{.*}$').test(button)) break

          this.identifier += button
          break
      }
    }
  }
})
</script>

<template>
  <v-sheet class="pa-7 pt-7" :width="'100%'" :min-width="350" :max-width="750" :elevation="4">
    <v-form @submit.prevent="doSubmit" validate-on="lazy">
      <v-container>
        <v-row>
          <v-col cols="12">
            <slot name="description"> </slot>
          </v-col>
        </v-row>
        <v-row>
          <v-btn-toggle
            divided
            color="primary"
            class="mx-auto"
            variant="plain"
            v-model="kioskState"
            @click="focusInput"
            mandatory
          >
            <v-btn :value="KioskState.BOC">
              <span class="hidden-sm-and-down">{{ buffCardActionText }}&nbsp;</span>
              <span>BuffOne</span>
            </v-btn>

            <v-btn :value="KioskState.SID">
              <span class="hidden-sm-and-down">Enter&nbsp;</span>
              <span>Student ID</span>
            </v-btn>

            <!-- currently we only have interest in privileged users identifying by identikey -->
            <!-- if this ever were to change, we'd remove this role check and change the "privileged" language -->
            <!-- we could always move the guards to the parent when determining props -->
            <v-btn
              v-if="privileged && userStore.hasOneOf([LIURole.EventAdmin, LIURole.EventProcessor])"
              :value="KioskState.IdentiKey"
            >
              <span class="hidden-sm-and-down">Enter&nbsp;</span>
              <span>IdentiKey</span>
            </v-btn>
          </v-btn-toggle>
        </v-row>
        <v-row>
          <v-col class="px-0">
            <v-text-field
              ref="identifierInputRef"
              v-model="identifier"
              :label="inputLabel"
              density="compact"
              variant="outlined"
              hide-details="auto"
              autocomplete="off"
              required
              autofocus
              :class="{ 'obfuscated-text': kioskState === KioskState.BOC }"
            >
            </v-text-field>
          </v-col>
        </v-row>
        <v-expand-transition>
          <v-row v-if="displayStore.isKiosk && kioskState !== KioskState.BOC">
            <SimpleKeyboard :layout="keypadLayout" @onKeyPress="handleKeyPress" />
          </v-row>
        </v-expand-transition>
        <v-row>
          <v-col cols="12" v-if="!hideButton" class="pb-0 mt-3">
            <div class="d-flex justify-center">
              <v-btn type="submit" color="primary" size="x-large" variant="tonal">{{
                buttonText
              }}</v-btn>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
  </v-sheet>
</template>

<style scoped>
.obfuscated-text :deep(input) {
  -webkit-text-security: disc !important;
}
</style>
